import {
  BasicBarChartConfig,
  BasicCartesianChartConfig,
  BasicScatterChartConfig,
} from '@app/data/interface/widget-config';
import {
  BasicBarChartColourPalette,
  BasicBarChartEchartConfigTemplate,
} from '@app/data/widget-config-templates/basic-bar-chart';
import {
  BasicCartesianChartEchartConfigTemplate,
  BasicCartesianChartAxisTemplate,
  BasicCartesianChartColourPalette,
} from '@app/data/widget-config-templates/basic-cartesian-chart';
import { BasicScatterChartEchartConfigTemplate } from '@app/data/widget-config-templates/basic-scatter-chart';

export const HarihariNutrients: BasicCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'TKN\ng/m³',
      },
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'P\ng/m³',
        position: 'right',
        axisLine: {
          show: true,
          lineStyle: {
            color: BasicCartesianChartColourPalette[1],
          },
        },
        axisLabel: {
          color: BasicCartesianChartColourPalette[1],
        },
      },
    ],
    series: [
      {
        type: 'line',
        yAxisIndex: 0,
        name: 'Total Kjeldahl Nitrogen',
        symbolSize: 13,
      },
      {
        type: 'line',
        yAxisIndex: 1,
        name: 'Total Phosphorus',
        symbolSize: 13,
      },
    ],
  },
  dataConfig: {
    title: 'Lake Harihari Nutrients',
    widgetId: 1,
    domain: 'wai',
    dataSources: [
      {
        environmentalDataSourceIdentifier: '1994/1',
        analyteIdentifier: 'total-kjeldahl-nitrogen',
        valueField: 'avg',
        label: 'Total Kjeldahl Nitrogen',
      },
      {
        environmentalDataSourceIdentifier: '1994/1',
        analyteIdentifier: 'total-phosphorus',
        valueField: 'avg',
        label: 'Total Phosphorus',
      },
    ],
  },
};

export const HarihariWaterChemistry: BasicCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'pH',
      },
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'TAN\ng/m³',
        position: 'right',
        axisLine: {
          show: true,
          lineStyle: {
            color: BasicCartesianChartColourPalette[1],
          },
        },
        axisLabel: {
          color: BasicCartesianChartColourPalette[1],
        },
      },
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'NNN\ng/m³',
        position: 'right',
        offset: 40,
        axisLine: {
          show: true,
          lineStyle: {
            color: BasicCartesianChartColourPalette[2],
          },
        },
        axisLabel: {
          color: BasicCartesianChartColourPalette[2],
        },
      },
    ],
    series: [
      {
        type: 'line',
        yAxisIndex: 0,
        name: 'pH',
        symbolSize: 13,
      },
      {
        type: 'line',
        yAxisIndex: 1,
        name: 'Total Ammoniacal Nitrogen',
        symbolSize: 13,
        tooltip: {
          valueFormatter: (value) => value + ' g/m³',
        },
      },
      {
        type: 'line',
        yAxisIndex: 2,
        name: 'Nitrate Nitrite Nitrogen',
        symbolSize: 13,
        tooltip: {
          valueFormatter: (value) => value + ' g/m³',
        },
      },
    ],
  },
  dataConfig: {
    title: 'Lake Harihari Water Chemistry',
    widgetId: 2,
    domain: 'wai',
    dataSources: [
      {
        environmentalDataSourceIdentifier: '1994/1',
        analyteIdentifier: 'ph-in-lab',
        valueField: 'avg',
        label: 'pH',
      },
      {
        environmentalDataSourceIdentifier: '1994/1',
        analyteIdentifier: 'total-ammoniacal-nitrogen',
        valueField: 'avg',
        label: 'Total Ammoniacal Nitrogen',
      },
      {
        environmentalDataSourceIdentifier: '1994/1',
        analyteIdentifier: 'nitrate-+-nitrite-nitrogen-grams-per-cubic-metre',
        valueField: 'avg',
        label: 'Nitrate Nitrite Nitrogen',
      },
    ],
  },
};

export const HarihariWaterClarity: BasicBarChartConfig = {
  echartsConfig: {
    ...BasicBarChartEchartConfigTemplate,
    tooltip: {
      ...BasicBarChartEchartConfigTemplate.tooltip,
      position: function (pt: any) {
        return [pt[0], '10%'];
      },
    },
    grid: {
      ...BasicBarChartEchartConfigTemplate.grid,
      left: '5%',
    },
    xAxis: {
      ...BasicBarChartEchartConfigTemplate.xAxis,
      boundaryGap: true,
    },
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'Turbidity\nNTU',
        offset: 40,
        boundaryGap: undefined,
        axisLine: {
          ...BasicCartesianChartAxisTemplate.axisLine,
          lineStyle: {
            color: BasicBarChartColourPalette[0],
          },
        },
        axisLabel: {
          color: BasicBarChartColourPalette[0],
        },
      },
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'TSS\ng/m³',
        boundaryGap: undefined,
        axisLine: {
          ...BasicCartesianChartAxisTemplate.axisLine,
          lineStyle: {
            color: BasicBarChartColourPalette[1],
          },
        },
        axisLabel: {
          color: BasicBarChartColourPalette[1],
        },
      },
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'Depth\nm',
        position: 'right',
        boundaryGap: undefined,
        axisLine: {
          ...BasicCartesianChartAxisTemplate.axisLine,
          lineStyle: {
            color: BasicBarChartColourPalette[2],
          },
        },
        axisLabel: {
          color: BasicBarChartColourPalette[2],
        },
      },
    ],
    series: [
      {
        type: 'bar',
        yAxisIndex: 0,
        name: 'Turbidity',
        tooltip: {
          valueFormatter: (value) => value + 'NTU',
        },
      },
      {
        type: 'bar',
        yAxisIndex: 1,
        name: 'Total Suspended Solids',
        tooltip: {
          valueFormatter: (value) => value + 'g/m³',
        },
      },
      {
        type: 'bar',
        yAxisIndex: 2,
        name: 'Secchi Depth',
        tooltip: {
          valueFormatter: (value) => value + 'm',
        },
      },
    ],
  },
  dataConfig: {
    title: 'Lake Harihari Water Clarity',
    widgetId: 5,
    domain: 'wai',
    boundary: 0.5,
    showBoundary: true,
    dataSources: [
      {
        environmentalDataSourceIdentifier: '1994/1',
        analyteIdentifier: 'turbidity',
        valueField: 'avg',
        label: 'Turbidity',
      },
      {
        environmentalDataSourceIdentifier: '1994/1',
        analyteIdentifier: 'total-suspended-solids',
        valueField: 'avg',
        label: 'Total Suspended Solids',
      },
      {
        environmentalDataSourceIdentifier: '1994/1',
        analyteIdentifier: 'setchi-disk',
        valueField: 'avg',
        label: 'Secchi Depth',
      },
    ],
  },
};

export const HarihariContaminants: BasicCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    grid: {
      ...BasicCartesianChartEchartConfigTemplate.grid,
      right: '7%',
    },
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'cfu/100ml',
        axisLine: {
          show: true,
          lineStyle: {
            color: BasicCartesianChartColourPalette[1],
          },
        },
        axisLabel: {
          color: BasicCartesianChartColourPalette[1],
        },
      },
    ],
    series: [
      {
        type: 'line',
        yAxisIndex: 0,
        name: 'E. coli',
        symbolSize: 13,
      },
    ],
  },
  dataConfig: {
    title: 'Lake Harihari Contaminants',
    widgetId: 1,
    domain: 'wai',
    dataSources: [
      {
        environmentalDataSourceIdentifier: '1994/1',
        analyteIdentifier: 'e.-coli',
        valueField: 'avg',
        label: 'E. coli',
      },
    ],
  },
};

export const HarihariTempChlorophyll: BasicScatterChartConfig = {
  echartsConfig: {
    ...BasicScatterChartEchartConfigTemplate,
    xAxis: {
      ...BasicScatterChartEchartConfigTemplate.xAxis,
      name: 'Temp\n°C',
    },
    yAxis: {
      ...BasicScatterChartEchartConfigTemplate.yAxis,
      name: 'Chlor. a\nμg/m³',
    },
  },
  dataConfig: {
    title: 'Lake Harihari Chlorophyll a Temperature Plot',
    widgetId: 5,
    domain: 'wai',
    xDataSource: {
      environmentalDataSourceIdentifier: '1994/1',
      analyteIdentifier: 'water-temperature',
      valueField: 'avg',
      label: 'Water Temperature',
    },
    yDataSource: {
      environmentalDataSourceIdentifier: '1994/1',
      analyteIdentifier: 'chlorophyll-a',
      valueField: 'avg',
      label: 'Chlorophyll a',
    },
  },
};

export const NumitiNutrients: BasicCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'TKN\ng/m³',
      },
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'P\ng/m³',
        position: 'right',
        axisLine: {
          show: true,
          lineStyle: {
            color: BasicCartesianChartColourPalette[1],
          },
        },
        axisLabel: {
          color: BasicCartesianChartColourPalette[1],
        },
      },
    ],
    series: [
      {
        type: 'line',
        yAxisIndex: 0,
        name: 'Total Kjeldahl Nitrogen',
        symbolSize: 13,
      },
      {
        type: 'line',
        yAxisIndex: 1,
        name: 'Total Phosphorus',
        symbolSize: 13,
      },
    ],
  },
  dataConfig: {
    title: 'Lake Numiti Nutrients',
    widgetId: 1,
    domain: 'wai',
    dataSources: [
      {
        environmentalDataSourceIdentifier: '3108/1',
        analyteIdentifier: 'total-kjeldahl-nitrogen',
        valueField: 'avg',
        label: 'Total Kjeldahl Nitrogen',
      },
      {
        environmentalDataSourceIdentifier: '3108/1',
        analyteIdentifier: 'total-phosphorus',
        valueField: 'avg',
        label: 'Total Phosphorus',
      },
    ],
  },
};

export const NumitiWaterChemistry: BasicCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    grid: {
      ...BasicBarChartEchartConfigTemplate.grid,
      right: '7%',
    },
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'pH',
      },
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'TAN\ng/m³',
        position: 'right',
        axisLine: {
          show: true,
          lineStyle: {
            color: BasicCartesianChartColourPalette[1],
          },
        },
        axisLabel: {
          color: BasicCartesianChartColourPalette[1],
        },
      },
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'NNN\ng/m³',
        position: 'right',
        offset: 40,
        axisLine: {
          show: true,
          lineStyle: {
            color: BasicCartesianChartColourPalette[2],
          },
        },
        axisLabel: {
          color: BasicCartesianChartColourPalette[2],
        },
      },
    ],
    series: [
      {
        type: 'line',
        yAxisIndex: 0,
        name: 'pH',
        symbolSize: 13,
      },
      {
        type: 'line',
        yAxisIndex: 1,
        name: 'Total Ammoniacal Nitrogen',
        symbolSize: 13,
        tooltip: {
          valueFormatter: (value) => value + ' g/m³',
        },
      },
      {
        type: 'line',
        yAxisIndex: 2,
        name: 'Nitrate Nitrite Nitrogen',
        symbolSize: 13,
        tooltip: {
          valueFormatter: (value) => value + ' g/m³',
        },
      },
    ],
  },
  dataConfig: {
    title: 'Lake Numiti Water Chemistry',
    widgetId: 2,
    domain: 'wai',
    dataSources: [
      {
        environmentalDataSourceIdentifier: '3108/1',
        analyteIdentifier: 'ph-in-lab',
        valueField: 'avg',
        label: 'pH',
      },
      {
        environmentalDataSourceIdentifier: '3108/1',
        analyteIdentifier: 'total-ammoniacal-nitrogen',
        valueField: 'avg',
        label: 'Total Ammoniacal Nitrogen',
      },
      {
        environmentalDataSourceIdentifier: '3108/1',
        analyteIdentifier: 'nitrate-+-nitrite-nitrogen-grams-per-cubic-metre',
        valueField: 'avg',
        label: 'Nitrate Nitrite Nitrogen',
      },
    ],
  },
};

export const NumitiWaterClarity: BasicBarChartConfig = {
  echartsConfig: {
    ...BasicBarChartEchartConfigTemplate,
    tooltip: {
      ...BasicBarChartEchartConfigTemplate.tooltip,
      position: function (pt: any) {
        return [pt[0], '10%'];
      },
    },
    grid: {
      ...BasicBarChartEchartConfigTemplate.grid,
      left: '5%',
    },
    xAxis: {
      ...BasicBarChartEchartConfigTemplate.xAxis,
      boundaryGap: true,
    },
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'Turbidity\nNTU',
        offset: 40,
        boundaryGap: undefined,
        axisLine: {
          ...BasicCartesianChartAxisTemplate.axisLine,
          lineStyle: {
            color: BasicBarChartColourPalette[0],
          },
        },
        axisLabel: {
          color: BasicBarChartColourPalette[0],
        },
      },
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'TSS\ng/m³',
        boundaryGap: undefined,
        axisLine: {
          ...BasicCartesianChartAxisTemplate.axisLine,
          lineStyle: {
            color: BasicBarChartColourPalette[1],
          },
        },
        axisLabel: {
          color: BasicBarChartColourPalette[1],
        },
      },
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'Depth\nm',
        position: 'right',
        boundaryGap: undefined,
        axisLine: {
          ...BasicCartesianChartAxisTemplate.axisLine,
          lineStyle: {
            color: BasicBarChartColourPalette[2],
          },
        },
        axisLabel: {
          color: BasicBarChartColourPalette[2],
        },
      },
    ],
    series: [
      {
        type: 'bar',
        yAxisIndex: 0,
        name: 'Turbidity',
        tooltip: {
          valueFormatter: (value) => value + 'NTU',
        },
      },
      {
        type: 'bar',
        yAxisIndex: 1,
        name: 'Total Suspended Solids',
        tooltip: {
          valueFormatter: (value) => value + 'g/m³',
        },
      },
      {
        type: 'bar',
        yAxisIndex: 2,
        name: 'Secchi Depth',
        tooltip: {
          valueFormatter: (value) => value + 'm',
        },
      },
    ],
  },
  dataConfig: {
    title: 'Lake Numiti Water Clarity',
    widgetId: 5,
    domain: 'wai',
    boundary: 0.5,
    showBoundary: true,
    dataSources: [
      {
        environmentalDataSourceIdentifier: '3108/1',
        analyteIdentifier: 'turbidity',
        valueField: 'avg',
        label: 'Turbidity',
      },
      {
        environmentalDataSourceIdentifier: '3108/1',
        analyteIdentifier: 'total-suspended-solids',
        valueField: 'avg',
        label: 'Total Suspended Solids',
      },
      {
        environmentalDataSourceIdentifier: '3108/1',
        analyteIdentifier: 'setchi-disk',
        valueField: 'avg',
        label: 'Secchi Depth',
      },
    ],
  },
};

export const NumitiContaminants: BasicCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    grid: {
      ...BasicCartesianChartEchartConfigTemplate.grid,
      right: '7%',
    },
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'cfu/100ml',
        axisLine: {
          show: true,
          lineStyle: {
            color: BasicCartesianChartColourPalette[1],
          },
        },
        axisLabel: {
          color: BasicCartesianChartColourPalette[1],
        },
      },
    ],
    series: [
      {
        type: 'line',
        yAxisIndex: 0,
        name: 'E. coli',
        symbolSize: 13,
      },
    ],
  },
  dataConfig: {
    title: 'Lake Numiti Contaminants',
    widgetId: 1,
    domain: 'wai',
    dataSources: [
      {
        environmentalDataSourceIdentifier: '3108/1',
        analyteIdentifier: 'e.-coli',
        valueField: 'avg',
        label: 'E. coli',
      },
    ],
  },
};

export const NumitiTempChlorophyll: BasicScatterChartConfig = {
  echartsConfig: {
    ...BasicScatterChartEchartConfigTemplate,
    xAxis: {
      ...BasicScatterChartEchartConfigTemplate.xAxis,
      name: 'Temp\n°C',
    },
    yAxis: {
      ...BasicScatterChartEchartConfigTemplate.yAxis,
      name: 'Chlor. a\nμg/m³',
    },
  },
  dataConfig: {
    title: 'Lake Numiti Chlorophyll a Temperature Plot',
    widgetId: 5,
    domain: 'wai',
    xDataSource: {
      environmentalDataSourceIdentifier: '3108/1',
      analyteIdentifier: 'water-temperature',
      valueField: 'avg',
      label: 'Water Temperature',
    },
    yDataSource: {
      environmentalDataSourceIdentifier: '3108/1',
      analyteIdentifier: 'chlorophyll-a',
      valueField: 'avg',
      label: 'Chlorophyll a',
    },
  },
};

export const RotoroaNutrients: BasicCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    grid: {
      ...BasicBarChartEchartConfigTemplate.grid,
      right: '-15%',
    },
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'TKN\ng/m³',
      },
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'P\ng/m³',
        position: 'right',
        axisLine: {
          show: true,
          lineStyle: {
            color: BasicCartesianChartColourPalette[1],
          },
        },
        axisLabel: {
          color: BasicCartesianChartColourPalette[1],
        },
      },
    ],
    series: [
      {
        type: 'line',
        yAxisIndex: 0,
        name: 'Total Kjeldahl Nitrogen',
        symbolSize: 13,
      },
      {
        type: 'line',
        yAxisIndex: 1,
        name: 'Total Phosphorus',
        symbolSize: 13,
      },
    ],
  },
  dataConfig: {
    title: 'Lake Rotoroa Nutrients',
    widgetId: 1,
    domain: 'wai',
    dataSources: [
      {
        environmentalDataSourceIdentifier: '3109/1',
        analyteIdentifier: 'total-kjeldahl-nitrogen',
        valueField: 'avg',
        label: 'Total Kjeldahl Nitrogen',
      },
      {
        environmentalDataSourceIdentifier: '3109/1',
        analyteIdentifier: 'total-phosphorus',
        valueField: 'avg',
        label: 'Total Phosphorus',
      },
    ],
  },
};

export const RotoroaWaterChemistry: BasicCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    grid: {
      ...BasicBarChartEchartConfigTemplate.grid,
      right: '-15%',
    },
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'pH',
      },
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'TAN\ng/m³',
        position: 'right',
        axisLine: {
          show: true,
          lineStyle: {
            color: BasicCartesianChartColourPalette[1],
          },
        },
        axisLabel: {
          color: BasicCartesianChartColourPalette[1],
        },
      },
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'NNN\ng/m³',
        position: 'right',
        offset: 40,
        axisLine: {
          show: true,
          lineStyle: {
            color: BasicCartesianChartColourPalette[2],
          },
        },
        axisLabel: {
          color: BasicCartesianChartColourPalette[2],
        },
      },
    ],
    series: [
      {
        type: 'line',
        yAxisIndex: 0,
        name: 'pH',
        symbolSize: 13,
      },
      {
        type: 'line',
        yAxisIndex: 1,
        name: 'Total Ammoniacal Nitrogen',
        symbolSize: 13,
        tooltip: {
          valueFormatter: (value) => value + ' g/m³',
        },
      },
      {
        type: 'line',
        yAxisIndex: 2,
        name: 'Nitrate Nitrite Nitrogen',
        symbolSize: 13,
        tooltip: {
          valueFormatter: (value) => value + ' g/m³',
        },
      },
    ],
  },
  dataConfig: {
    title: 'Lake Rotoroa Water Chemistry',
    widgetId: 2,
    domain: 'wai',
    dataSources: [
      {
        environmentalDataSourceIdentifier: '3109/1',
        analyteIdentifier: 'ph-in-lab',
        valueField: 'avg',
        label: 'pH',
      },
      {
        environmentalDataSourceIdentifier: '3109/1',
        analyteIdentifier: 'total-ammoniacal-nitrogen',
        valueField: 'avg',
        label: 'Total Ammoniacal Nitrogen',
      },
      {
        environmentalDataSourceIdentifier: '3109/1',
        analyteIdentifier: 'nitrate-+-nitrite-nitrogen-grams-per-cubic-metre',
        valueField: 'avg',
        label: 'Nitrate Nitrite Nitrogen',
      },
    ],
  },
};

export const RotoroaWaterClarity: BasicBarChartConfig = {
  echartsConfig: {
    ...BasicBarChartEchartConfigTemplate,
    tooltip: {
      ...BasicBarChartEchartConfigTemplate.tooltip,
      position: function (pt: any) {
        return [pt[0], '10%'];
      },
    },
    grid: {
      ...BasicBarChartEchartConfigTemplate.grid,
      left: '5%',
    },
    xAxis: {
      ...BasicBarChartEchartConfigTemplate.xAxis,
      boundaryGap: true,
    },
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'Turbidity\nNTU',
        offset: 40,
        boundaryGap: undefined,
        axisLine: {
          ...BasicCartesianChartAxisTemplate.axisLine,
          lineStyle: {
            color: BasicBarChartColourPalette[0],
          },
        },
        axisLabel: {
          color: BasicBarChartColourPalette[0],
        },
      },
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'TSS\ng/m³',
        boundaryGap: undefined,
        axisLine: {
          ...BasicCartesianChartAxisTemplate.axisLine,
          lineStyle: {
            color: BasicBarChartColourPalette[1],
          },
        },
        axisLabel: {
          color: BasicBarChartColourPalette[1],
        },
      },
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'Depth\nm',
        position: 'right',
        boundaryGap: undefined,
        axisLine: {
          ...BasicCartesianChartAxisTemplate.axisLine,
          lineStyle: {
            color: BasicBarChartColourPalette[2],
          },
        },
        axisLabel: {
          color: BasicBarChartColourPalette[2],
        },
      },
    ],
    series: [
      {
        type: 'bar',
        yAxisIndex: 0,
        name: 'Turbidity',
        tooltip: {
          valueFormatter: (value) => value + 'NTU',
        },
      },
      {
        type: 'bar',
        yAxisIndex: 1,
        name: 'Total Suspended Solids',
        tooltip: {
          valueFormatter: (value) => value + 'g/m³',
        },
      },
      {
        type: 'bar',
        yAxisIndex: 2,
        name: 'Secchi Depth',
        tooltip: {
          valueFormatter: (value) => value + 'm',
        },
      },
    ],
  },
  dataConfig: {
    title: 'Lake Rotoroa Water Clarity',
    widgetId: 5,
    domain: 'wai',
    boundary: 0.5,
    showBoundary: true,
    dataSources: [
      {
        environmentalDataSourceIdentifier: '3109/1',
        analyteIdentifier: 'turbidity',
        valueField: 'avg',
        label: 'Turbidity',
      },
      {
        environmentalDataSourceIdentifier: '3109/1',
        analyteIdentifier: 'total-suspended-solids',
        valueField: 'avg',
        label: 'Total Suspended Solids',
      },
      {
        environmentalDataSourceIdentifier: '3109/1',
        analyteIdentifier: 'setchi-disk',
        valueField: 'avg',
        label: 'Secchi Depth',
      },
    ],
  },
};

export const RotoroaContaminants: BasicCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    grid: {
      ...BasicCartesianChartEchartConfigTemplate.grid,
      right: '7%',
    },
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'cfu/100ml',
        axisLine: {
          show: true,
          lineStyle: {
            color: BasicCartesianChartColourPalette[1],
          },
        },
        axisLabel: {
          color: BasicCartesianChartColourPalette[1],
        },
      },
    ],
    series: [
      {
        type: 'line',
        yAxisIndex: 0,
        name: 'E. coli',
        symbolSize: 13,
      },
    ],
  },
  dataConfig: {
    title: 'Lake Rotoroa Contaminants',
    widgetId: 1,
    domain: 'wai',
    dataSources: [
      {
        environmentalDataSourceIdentifier: '3109/1',
        analyteIdentifier: 'e.-coli',
        valueField: 'avg',
        label: 'E. coli',
      },
    ],
  },
};

export const RotoroaTempChlorophyll: BasicScatterChartConfig = {
  echartsConfig: {
    ...BasicScatterChartEchartConfigTemplate,
    xAxis: {
      ...BasicScatterChartEchartConfigTemplate.xAxis,
      name: 'Temp\n°C',
    },
    yAxis: {
      ...BasicScatterChartEchartConfigTemplate.yAxis,
      name: 'Chlor. a\nμg/m³',
    },
  },
  dataConfig: {
    title: 'Lake Rotoroa Chlorophyll a Temperature Plot',
    widgetId: 5,
    domain: 'wai',
    xDataSource: {
      environmentalDataSourceIdentifier: '3109/1',
      analyteIdentifier: 'water-temperature',
      valueField: 'avg',
      label: 'Water Temperature',
    },
    yDataSource: {
      environmentalDataSourceIdentifier: '3109/1',
      analyteIdentifier: 'chlorophyll-a',
      valueField: 'avg',
      label: 'Chlorophyll a',
    },
  },
};

export const WaterLevels: BasicBarChartConfig = {
  echartsConfig: {
    ...BasicBarChartEchartConfigTemplate,
    tooltip: {
      ...BasicBarChartEchartConfigTemplate.tooltip,
      position: function (pt: any) {
        return [pt[0], '10%'];
      },
    },
    xAxis: {
      ...BasicBarChartEchartConfigTemplate.xAxis,
      boundaryGap: true,
    },
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'Metres',
        boundaryGap: undefined,
        axisLine: {
          show: true,
        },
        axisLabel: {
          color: '#008ACD',
        },
      },
    ],
    series: [
      {
        type: 'bar',
        yAxisIndex: 0,
        name: 'Lake Harihari',
      },
      {
        type: 'bar',
        yAxisIndex: 0,
        name: 'Lake Numiti',
      },
      {
        type: 'bar',
        yAxisIndex: 0,
        name: 'Lake Rotoroa',
      },
    ],
  },
  dataConfig: {
    title: 'Water Levels',
    widgetId: 3,
    domain: 'wai',
    showBoundary: true,
    boundary: 0.5,
    dataSources: [
      {
        environmentalDataSourceIdentifier: '1994/1',
        analyteIdentifier: 'relative-water-level',
        valueField: 'avg',
        label: 'Lake Harihari',
      },
      {
        environmentalDataSourceIdentifier: '3108/1',
        analyteIdentifier: 'relative-water-level',
        valueField: 'avg',
        label: 'Lake Numiti',
      },
      {
        environmentalDataSourceIdentifier: '3109/1',
        analyteIdentifier: 'relative-water-level',
        valueField: 'avg',
        label: 'Lake Rotoroa',
      },
    ],
  },
};
